import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useFilters, VEG_NOTE, PRACTICE_NOTE } from "@/context/FiltersContext";
import { slugKey } from "@/lib/constants";
import { AllergenIcon } from "@/components/z6/AllergenIcon";
import { AllergenKey } from "@/components/z6/AllergenKey";
import { CrossContact } from "@/components/z6/FilteredResults";

const VEG = [["all", "All"], ["veg", "Veg"], ["nonveg", "Non-veg"], ["vegan", "Vegan"]];
const LOCK_LABEL = { veg: "excluded by Veg", vegan: "excluded by Vegan" };

const Chip = ({ active, onClick, children, testId, icon, locked }) => (
  <motion.button layout type="button" whileTap={locked ? undefined : { scale: 0.92 }} onClick={locked ? undefined : onClick} data-testid={testId} aria-pressed={active} aria-disabled={locked || undefined}
    transition={{ type: "spring", stiffness: 500, damping: 30 }}
    className={`font-ui inline-flex min-h-[48px] items-center gap-2 rounded-full border px-4 text-[15px] transition-[background-color,border-color,color,box-shadow] duration-300 ${
      locked ? "cursor-not-allowed border-[#0B5C8A]/40 bg-[#0B5C8A]/80 text-white" :
      active ? "border-[#0B5C8A] bg-transparent ring-1 ring-[#E8508A] hover:bg-[#E8508A]/10 text-[#B8326C] shadow-[0_8px_20px_-10px_rgba(11,92,138,0.6)] ring-1 ring-[#F08DB5]/55" : "border-[#0B2E44]/15 bg-white/70 text-[#0B2E44]"}`}>
    {icon && <AllergenIcon name={icon} className="h-4 w-4" />}
    {children}
    {locked && <span className="inline-flex items-center gap-1 text-[15px] opacity-90"><Lock className="h-3 w-3" />{locked}</span>}
  </motion.button>
);

const Group = ({ title, hint, children, aside }) => (
  <section className="space-y-3">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h4 className="font-display text-[28px] leading-none text-[#0B2E44]">{title}</h4>
        {hint && <p className="font-ui mt-1 text-[15px] text-[#0B2E44]">{hint}</p>}
      </div>
      {aside}
    </div>
    <motion.div layout className="flex flex-wrap gap-2">{children}</motion.div>
  </section>
);

export const FilterPanel = ({ open, onClose, resultCount, drinks }) => {
  const { filters, toggle, setVeg, clear: clearAll, activeCount: allCount, locked: vegLocked, options } = useFilters();
  const avoidCount = filters.avoid.filter((a) => options.allergens.includes(a)).length;
  const activeCount = drinks ? avoidCount + drinks.f.cats.length + drinks.f.temps.length + (drinks.f.plant ? 1 : 0) : allCount;
  const clear = drinks ? drinks.onClear : clearAll;
  const locked = drinks ? [] : vegLocked;
  const flip = (k, v) => drinks.set((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  useEffect(() => {
    if (!open) return undefined;
    document.body.dataset.modal = "1";
    return () => { delete document.body.dataset.modal; };
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 1 }} exit={{ opacity: 1 }}>
          <motion.div className="absolute inset-0 bg-[#0B2E44]/20 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside data-testid="filter-panel" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 300, damping: 34 }}
            className="panel-glass absolute right-0 top-0 flex h-full w-full flex-col sm:w-[520px]">
            <div className="flex items-start justify-between border-b border-[#0B2E44]/15 px-7 py-6">
              <div>
                <p className="font-mono text-[15px] uppercase tracking-[0.3em] text-[#0B2E44]">{drinks ? "Every drink" : "Across every menu"}</p>
                <h3 className="mt-1 font-display text-5xl text-[#0B2E44]">Refine</h3>
              </div>
              <button data-testid="filter-close-btn" onClick={onClose} aria-label="Close filters" className="flex h-12 w-12 items-center justify-center rounded-full border border-[#0B2E44]/15 text-[#0B2E44]">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <div className="flex-1 space-y-9 overflow-y-auto px-7 py-7">
              {drinks ? (
                <>
                  <Group title="Drink category">
                    {drinks.sections.map((s) => <Chip key={s} active={drinks.f.cats.includes(s)} onClick={() => flip("cats", s)} testId={`filter-drink-cat-${slugKey(s)}`}>{s}</Chip>)}
                  </Group>
                  <Group title="Serving temperature">
                    {drinks.temps.map(([k, l]) => <Chip key={k} active={drinks.f.temps.includes(k)} onClick={() => flip("temps", k)} testId={`filter-drink-temp-${slugKey(l)}`}>{l}</Chip>)}
                  </Group>
                  <Group title="Plant-based" hint="Drinks listed as vegan">
                    <Chip active={drinks.f.plant} onClick={() => drinks.set((f) => ({ ...f, plant: !f.plant }))} testId="filter-drink-plant">Plant-based only</Chip>
                  </Group>
                </>
              ) : (
                <>
                  <Group title="Diet">
                    {VEG.map(([k, l]) => <Chip key={k} active={filters.veg === k} onClick={() => setVeg(k)} testId={`filter-veg-${k}`}>{l}</Chip>)}
                  </Group>
                  {["veg", "vegan"].includes(filters.veg) && <p data-testid="filter-veg-note" className="font-ui -mt-5 rounded-2xl bg-[#FFF4EF] px-4 py-3 text-[15px] text-[#0B2E44]">{VEG_NOTE}</p>}
                </>
              )}
              <Group title="Allergens to avoid" hint="We show dishes made without these" aside={<AllergenKey testId="filter-allergen-key" />}>
                {options.allergens.map((a) => (
                  <Chip key={a} icon={a} active={filters.avoid.includes(a) || locked.includes(a)} locked={locked.includes(a) ? LOCK_LABEL[filters.veg] : null}
                    onClick={() => toggle("avoid", a)} testId={`filter-chip-allergen-${slugKey(a)}`}>{a}</Chip>
                ))}
              </Group>
              {!drinks && (
                <>
                  <Group title="Made without" hint="Religious and cultural preferences">
                    {options.practices.map((p) => (
                      <Chip key={p} active={filters.avoid.includes(p)} onClick={() => toggle("avoid", p)} testId={`filter-chip-practice-${slugKey(p)}`}>{p}</Chip>
                    ))}
                  </Group>
                  <p data-testid="filter-practice-note" className="font-ui -mt-5 text-[15px] text-[#0B2E44]/80">{PRACTICE_NOTE}</p>
                </>
              )}
              <CrossContact testId="filter-cross-contact" />
            </div>
            <div className="flex items-center gap-3 border-t border-[#0B2E44]/15 px-7 py-5">
              <button data-testid="filter-clear-all-btn" onClick={clear} disabled={!activeCount} className="font-ui min-h-[48px] rounded-full px-5 text-[15px] text-[#0B2E44] disabled:opacity-40">Clear all</button>
              <motion.button whileTap={{ scale: 0.97 }} data-testid="filter-apply-btn" onClick={onClose}
                className="font-ui min-h-[52px] flex-1 rounded-full bg-transparent ring-1 ring-[#E8508A] hover:bg-[#E8508A]/10 px-6 text-[16px] font-normal text-[#B8326C]">
                {activeCount ? `Show ${resultCount} ${drinks ? "drinks" : "dishes"}` : "Done"}
              </motion.button>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
